import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'antd';
import { jwtDecode } from 'jwt-decode';
import type { SeriesBySensor, TelemetryVital } from '@/apis/telemetry';
import type { SensorReportShipment } from '@/apis/shipmentEvents';
import theme from '@/assets/theme';
import { getActiveToken } from '@/common/helper/tokenStorage';
import { chartModel } from '../lib/chartModel';
import type { Device } from '../lib/devices';
import { eventsIn, type ShippingEvent } from '../lib/events';
import type { Extent } from '../lib/extent';
import { stackGeometry } from '../lib/layout';
import { paginate, reportKeyInfo, type ReportBlock } from '../lib/report';
import type { RangeView } from '../lib/range';
import { formatReadingTime } from '../lib/time';
import { ChunkStatus } from '../queries';
import { useLabelMeasure } from '../useLabelMeasure';
import type { RangeSelection } from '../useRangeSelection';
import VitalChart from './VitalChart';
import styles from './PdfPreview.module.css';

/** Width of a Letter page inside 12 mm margins, in CSS pixels (191.9 mm at 96 px per inch). */
export const REPORT_WIDTH = 725;

/** Height a page's blocks may fill, in CSS pixels: 255 mm of the 255.4 mm inside the margins, less the footer. */
export const REPORT_BODY_HEIGHT = 920;

const BLOCK_GAP = 16;

const PRINT_CSS = `
@page { size: letter portrait; margin: 12mm; }
@media print {
  html, body { height: auto !important; overflow: visible !important; }
  body > :not([data-sensor-report]) { display: none !important; }
}`;

const NO_HANDLERS = (): void => undefined;

/** Props for {@link PdfPreview}. */
export interface PdfPreviewProps {
  shipment: SensorReportShipment;
  rangeName: string;
  view: RangeView;
  range: RangeSelection;
  charts: readonly TelemetryVital[];
  chartStatus: Partial<Record<TelemetryVital, ChunkStatus>>;
  devices: readonly Device[];
  series: SeriesBySensor;
  notLoaded: Partial<Record<TelemetryVital, Extent[]>>;
  hidden: ReadonlySet<string>;
  events: readonly ShippingEvent[];
  timeZone: string;
  onClose: () => void;
}

type Block =
  | { kind: 'header' }
  | { kind: 'chart'; index: number }
  | { kind: 'event'; index: number }
  | { kind: 'noEvents' };

const viewerName = (): string => {
  const token = getActiveToken();
  if (!token) return 'Shared link';
  try {
    return jwtDecode<{ name?: string }>(token).name ?? '';
  } catch {
    return '';
  }
};

const NONE: Extent[] = [];

/**
 * The sensor report, previewed in the app and printed through the browser: key info, the visible devices, every
 * selected chart at report width with the shipping events always drawn, and the events table, laid out on Letter
 * pages so no chart or table row splits across a page break (PRD §4.1, §5.8). Save as PDF opens the print dialog,
 * which prints only the report; Close leaves the modal as it was.
 */
const PdfPreview = ({
  shipment,
  rangeName,
  view,
  range,
  charts,
  chartStatus,
  devices,
  series,
  notLoaded,
  hidden,
  events,
  timeZone,
  onClose,
}: PdfPreviewProps) => {
  const [generatedAt] = useState(() => Date.now());
  const [generatedBy] = useState(viewerName);
  const { window } = range;
  const marked = view.marked && !range.zoomed;
  const shown = useMemo(
    () => devices.filter((device) => !hidden.has(device.id)),
    [devices, hidden]
  );
  const models = useMemo(
    () => charts.map((key) => chartModel(key, devices, series, window, hidden)),
    [charts, devices, series, window, hidden]
  );
  const measure = useLabelMeasure();
  const geometry = useMemo(
    () => stackGeometry(models, window, REPORT_WIDTH, timeZone, measure),
    [models, window, timeZone, measure]
  );
  const rows = useMemo(() => eventsIn(events, window), [events, window]);
  const keyInfo = reportKeyInfo({
    shipment,
    pickedUp: view.pickedUp,
    delivered: view.delivered,
    rangeName,
    window,
    devices: shown,
    charts,
    generatedBy,
    generatedAt,
    timeZone,
  });

  const blocks = useMemo<Block[]>(
    () => [
      { kind: 'header' },
      ...models.map((_, index): Block => ({ kind: 'chart', index })),
      ...(rows.length > 0
        ? rows.map((_, index): Block => ({ kind: 'event', index }))
        : [{ kind: 'noEvents' } as const]),
    ],
    [models, rows]
  );

  const firstEventBlock = 1 + models.length;
  const measured = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<{
    blocks: Block[];
    pages: number[][];
  } | null>(null);
  const pages = layout?.blocks === blocks ? layout.pages : null;
  useLayoutEffect(() => {
    const root = measured.current;
    if (!root) return;
    const heightOf = (selector: string): number =>
      root.querySelector<HTMLElement>(selector)?.offsetHeight ?? 0;
    const headHeight = root.querySelector('thead')?.offsetHeight ?? 0;
    const titleHeight = heightOf('[data-block="event-title"]');
    const sized: ReportBlock[] = blocks.map((block, i) => {
      const own = heightOf(`[data-block="${i}"]`);
      if (block.kind !== 'event') return { height: own + BLOCK_GAP };
      return block.index === 0
        ? { height: own + titleHeight + headHeight + BLOCK_GAP }
        : { height: own, carryHeight: headHeight + BLOCK_GAP };
    });
    setLayout({ blocks, pages: paginate(sized, REPORT_BODY_HEIGHT) });
  }, [blocks]);

  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => dialog.current?.focus(), []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = () => {
    const title = document.title;
    const restore = () => {
      document.title = title;
      globalThis.removeEventListener('afterprint', restore);
    };
    document.title = ['Sensor report', shipment.reference_number]
      .filter(Boolean)
      .join(' ');
    globalThis.addEventListener('afterprint', restore);
    globalThis.print();
  };

  const header = (
    <header className={styles.header}>
      <div className={styles.brand}>
        {theme.themeType === 1 ? (
          <span className={styles.mark}>{theme.title}</span>
        ) : (
          <img
            src={theme.headerLogo}
            className={styles.logo}
            alt={theme.title}
          />
        )}
        <h1 className={styles.title}>Sensor report</h1>
      </div>
      <dl className={styles.keyInfo}>
        {keyInfo.map((field) => (
          <div key={field.label} className={styles.field}>
            <dt>{field.label}</dt>
            <dd>{field.value}</dd>
          </div>
        ))}
      </dl>
      <ul className={styles.legend} aria-label='Devices'>
        {shown.map((device) => (
          <li key={device.id}>
            <span
              className={styles.swatch}
              // eslint-disable-next-line react/forbid-dom-props -- the device colour is a runtime value
              style={{ backgroundColor: device.color }}
            />
            {device.label}
          </li>
        ))}
      </ul>
    </header>
  );

  const chart = (index: number) => {
    const model = models[index];
    return (
      <VitalChart
        model={model}
        status={chartStatus[model.measure.key] ?? ChunkStatus.Loading}
        width={REPORT_WIDTH}
        marginLeft={geometry.marginLeft}
        xScale={geometry.xScale}
        xTicks={geometry.xTicks}
        yAxis={geometry.yAxes[index]}
        marked={marked}
        selection={range.selection}
        notLoaded={notLoaded[model.measure.key] ?? NONE}
        origins={range.origins}
        labelOrigins={index === 0 && marked}
        note={null}
        handles={null}
        onZoomOut={null}
        hoverAt={null}
        events={rows}
        activeEventId={null}
        onHover={NO_HANDLERS}
        onHoverEnd={NO_HANDLERS}
      />
    );
  };

  const eventsTitle = <h2 className={styles.sectionTitle}>Shipping events</h2>;
  const eventsHead = (
    <thead>
      <tr>
        <th>Time</th>
        <th>Event</th>
        <th>Location</th>
      </tr>
    </thead>
  );
  const eventRow = (index: number) => {
    const event = rows[index];
    return (
      <tr key={event.id}>
        <td>{formatReadingTime(event.at, timeZone)}</td>
        <td>{event.action}</td>
        <td>{event.location}</td>
      </tr>
    );
  };

  const content = (block: Block): ReactNode => {
    if (block.kind === 'header') return header;
    if (block.kind === 'chart') return chart(block.index);
    if (block.kind === 'noEvents') {
      return (
        <>
          {eventsTitle}
          <p className={styles.empty}>No shipping events in this range.</p>
        </>
      );
    }
    return null;
  };

  const page = (indexes: readonly number[]): ReactNode[] => {
    const nodes: ReactNode[] = [];
    let eventRows: number[] = [];
    const flushEvents = () => {
      if (eventRows.length === 0) return;
      const startsTable = eventRows[0] === 0;
      nodes.push(
        <div key={`events-${eventRows[0]}`} className={styles.block}>
          {startsTable && eventsTitle}
          <table className={styles.events}>
            {eventsHead}
            <tbody>{eventRows.map(eventRow)}</tbody>
          </table>
        </div>
      );
      eventRows = [];
    };
    for (const i of indexes) {
      const block = blocks[i];
      if (block.kind === 'event') {
        eventRows.push(block.index);
        continue;
      }
      flushEvents();
      nodes.push(
        <div key={i} className={styles.block}>
          {content(block)}
        </div>
      );
    }
    flushEvents();
    return nodes;
  };

  const source = ['Source: Zoomlogi', shipment.reference_number]
    .filter(Boolean)
    .join(' · ');

  return createPortal(
    <div
      className={`${styles.preview} zl-force-light-theme`}
      ref={dialog}
      tabIndex={-1}
      data-sensor-report
      role='dialog'
      aria-modal='true'
      aria-label='PDF preview'
    >
      <style>{PRINT_CSS}</style>
      <div className={styles.bar}>
        <span className={styles.barTitle}>PDF preview</span>
        <div className={styles.actions}>
          <Button onClick={onClose}>Close</Button>
          <Button type='primary' disabled={!pages} onClick={save}>
            Save as PDF
          </Button>
        </div>
      </div>
      <div className={styles.sheets}>
        {pages ? (
          pages.map((indexes, number) => (
            <section
              key={indexes[0]}
              className={styles.page}
              aria-label={`Page ${number + 1}`}
            >
              <div className={styles.body}>{page(indexes)}</div>
              <footer className={styles.footer}>
                <span>{source}</span>
                <span>
                  Page {number + 1} of {pages.length}
                </span>
              </footer>
            </section>
          ))
        ) : (
          <div ref={measured} className={styles.measure} aria-hidden>
            {blocks.map((block, i) =>
              block.kind === 'event' ? null : (
                // eslint-disable-next-line react/no-array-index-key -- the index is the block's identity, shared with data-block
                <div key={i} data-block={i} className={styles.block}>
                  {content(block)}
                </div>
              )
            )}
            {rows.length > 0 && (
              <div className={styles.block}>
                <div data-block='event-title'>{eventsTitle}</div>
                <table className={styles.events}>
                  {eventsHead}
                  <tbody>
                    {rows.map((event, index) => (
                      <tr key={event.id} data-block={firstEventBlock + index}>
                        <td>{formatReadingTime(event.at, timeZone)}</td>
                        <td>{event.action}</td>
                        <td>{event.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PdfPreview;
